import { useState, type FormEvent } from 'react';
import { Languages } from 'lucide-react';
import { ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useI18n } from '../lib/i18n';
import { VigLogo } from '../components/Logo';
import { Button, Field, Input } from '../components/ui';

export function LoginPage() {
  const { t, locale, setLocale } = useI18n();
  const { signIn } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signIn(username, password);
    } catch (err) {
      // Said in the reader's language rather than the server's words.
      setError(
        err instanceof ApiError
          ? err.code === 'INVALID_CREDENTIALS'
            ? t('auth.invalid')
            : err.code === 'ACCOUNT_DISABLED'
              ? t('auth.disabled')
              : err.message
          : t('common.error'),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-theme="dark" className="relative grid min-h-full place-items-center overflow-hidden bg-ink-deep px-4 py-10">
      {/* The dark hero treatment: mesh field over the machine grid. */}
      <div className="pointer-events-none absolute inset-0 bg-mesh animate-mesh-drift" aria-hidden />
      <div className="pointer-events-none absolute inset-0 bg-grid-dark bg-grid-size opacity-60" aria-hidden />

      <button
        type="button"
        onClick={() => setLocale(locale === 'en' ? 'ar' : 'en')}
        className="absolute end-4 top-4 z-10 inline-flex items-center gap-2 rounded-md border border-ink-line bg-ink-card/70 px-3 py-1.5 text-body-sm text-neutral-300 backdrop-blur hover:text-neutral-50"
      >
        <Languages className="h-4 w-4" aria-hidden />
        {locale === 'en' ? 'العربية' : 'English'}
      </button>

      <div className="relative w-full max-w-sm">
        <div className="mb-7 text-center">
          <h1>
            <VigLogo className="mx-auto h-11 w-auto text-neutral-50" />
            <span className="sr-only">{t('app.name')}</span>
          </h1>
          <p className="mt-3 text-mono-sm uppercase tracking-widest text-[var(--text-tertiary)]">{t('app.surface')}</p>
          <p className="mt-3 text-body-sm text-neutral-400">{t('auth.subtitle')}</p>
        </div>

        <form
          onSubmit={onSubmit}
          className="rounded-xl border border-ink-line bg-ink-card/90 p-6 shadow-edge-neutral backdrop-blur"
        >
          <h2 className="mb-5 font-display text-h2 text-neutral-50">{t('auth.welcome')}</h2>

          <div className="space-y-4">
            <Field label={<span className="text-neutral-300">{t('auth.usernameLabel')}</span>} required>
              <Input
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                required
                autoFocus
                dir="ltr"
                className="!bg-ink-raised !border-ink-line !text-neutral-50 placeholder:!text-[var(--text-tertiary)] dark:placeholder:!text-[var(--text-secondary)]"
              />
            </Field>

            <Field label={<span className="text-neutral-300">{t('auth.passwordLabel')}</span>} required>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                required
                dir="ltr"
                className="!bg-ink-raised !border-ink-line !text-neutral-50"
              />
            </Field>
          </div>

          {error && (
            <p
              role="alert"
              className="mt-4 rounded-md border border-error/30 bg-error/10 px-3 py-2 text-body-sm text-error-dark"
            >
              {error}
            </p>
          )}

          <Button type="submit" variant="primary" size="lg" loading={busy} className="mt-5 w-full">
            {busy ? t('auth.signingIn') : t('auth.signIn')}
          </Button>

          <p className="mt-4 text-caption text-[var(--text-tertiary)]">{t('auth.forgot')}</p>
        </form>

        <p className="mt-5 text-center text-caption text-[var(--text-tertiary)]">
          {t('app.name')} · {t('app.tagline')}
        </p>
      </div>
    </div>
  );
}
